import { useTranslation } from 'react-i18next';
import React, { useState } from 'react';
import Box from 'Components/Atoms/Box';
import ButtonBase from '@material-ui/core/ButtonBase';
import { useHistory } from 'react-router-dom';
import Typography from 'Components/Atoms/Typography';
import { classHelper } from 'utils/helper';
import './styles.scss';
import useMediaQuery from '@material-ui/core/useMediaQuery';
import usePinAuth, { SecurityPlusKeys } from 'CustomHooks/usePinAuth';
import LockedProductIcon from 'Components/Atoms/Icons/LockedProduct';
import { AppId } from 'types/shared/documents/restaurants';
import { Translations } from 'utils/types';
import useRestaurant from 'CustomHooks/useRestaurant';

export type ProductIconProps = {
  id: string;
  label: string;
  notifications?: null | number;
  linkTo?: (restaurantId: string, apps: AppId[]) => string;
  restaurantId: string | null;
  hide?: boolean;
  hideLabel?: string;
  isNew?: boolean;
  className?: string;
  locked?: boolean;
  authorize?: SecurityPlusKeys;
  promotionalLink?: Function;
  onClick?: (rId: string | null) => void | Promise<void>;
  /** After a successful PIN check, right before the product opens. */
  onActivated?: () => void;
  /** null for a label that is data, not a translation key. */
  translation?: Translations | null;
};

// Unique per mounted icon (React 17 has no useId).
let lockIdCounter = 0;

const ProductIcon: React.FC<ProductIconProps> = ({
  id,
  linkTo,
  label,
  restaurantId,
  hide = false,
  hideLabel,
  isNew = false,
  className,
  locked = false,
  authorize,
  promotionalLink,
  onClick,
  onActivated,
  notifications,
  translation = 'products',
}) => {
  const { t } = useTranslation();
  const { apps } = useRestaurant();

  const history = useHistory();
  const isMobile = useMediaQuery('(max-width: 450px)');

  const { authenticate } = usePinAuth();

  const [lockId] = useState(() => `product-lock-${id}-${++lockIdCounter}`);
  const showLock = locked && !hide;

  if (id === 'filler-box') {
    return <Box width="80px"></Box>;
  }

  const showNotifications =
    typeof notifications === 'number' && notifications > 0;
  const showNewBadge =
    isNew && !showNotifications && typeof notifications !== 'number';

  return (
    <Box
      flex
      className={classHelper([
        'inline-column',
        'product-icon',
        hide && 'hidden',
        isNew && 'isNew',
        className,
        locked && !hide && 'isLocked',
      ])}
    >
      <ButtonBase
        style={{ marginBottom: 8, color: '#fff' }}
        aria-label={translation ? t(label, { ns: translation }) : label}
        // The button's own label hides the badge's; the description carries it.
        aria-describedby={showLock ? lockId : undefined}
        href={
          !!hide && !!hideLabel
            ? 'https://gastronomen.gastronaut.ai/module#coming'
            : id === 'faq'
            ? 'https://get.gastronaut.ai/help-center/help-normal'
            : ''
        }
        target="_blank"
        onClick={
          !hide
            ? () =>
                authenticate(authorize ?? 'none', () => {
                  onActivated?.();
                  if (linkTo) {
                    return history?.push(linkTo(restaurantId || '', apps));
                  } else if (onClick) {
                    return onClick(restaurantId);
                  }
                })
            : promotionalLink
            ? () => history?.push(promotionalLink(restaurantId))
            : undefined
        }
      >
        {!locked && showNotifications && (
          <span className="new-badge">{notifications}</span>
        )}
        {!locked && showNewBadge && <span className="new-badge">New</span>}
        {showLock && (
          <LockedProductIcon
            id={lockId}
            role="img"
            aria-label={t('pinProtectionActive', { ns: 'common' })}
            strokeColor="var(--color-bg)"
            className="locked-icon"
            width="32"
            height="32"
          />
        )}
        <Box
          flex
          className={classHelper(['center', isMobile && 'mobile-version'])}
          padding={20}
          elevation={0}
        >
          <img
            height={40}
            width={40}
            alt={id}
            src={
              !isMobile
                ? `/products/${id}.svg`
                : hide
                ? `/products/mobile/${id}.svg`
                : `/products/${id}.svg`
            }
          />
          {!!hide && !!hideLabel && (
            <Typography
              className="hideLabel"
              variant="text-3"
              textAlign="center"
              translation={null}
            >
              {hideLabel}
            </Typography>
          )}
        </Box>
      </ButtonBase>
      {!isMobile && (
        <Typography
          variant="text-3"
          textAlign="center"
          translation={translation}
          style={{ whiteSpace: 'nowrap' }}
        >
          {label}
        </Typography>
      )}
    </Box>
  );
};

export default ProductIcon;
